import { useEffect, useState } from 'react';
import { api } from '../api';
import { shortDate } from '../format';

type MaintenanceGrant = { id: string; expiresAt: string; revoked: boolean; createdAt: string };

/**
 * Lien temporaire pour qu'une intervenante (ex. Aurélie) déclare elle-même une
 * réparation avec sa photo, sans compte partagé. Même dispositif que les liens
 * EDL : jeton à usage limité, révocable, jamais un accès permanent.
 */
export function MaintenanceAccessPanel({ embedded = false }: {
  /** Posé dans une fenêtre : le bloc dépliant n'a plus lieu d'être. */
  embedded?: boolean;
} = {}) {
  const [grants, setGrants] = useState<MaintenanceGrant[] | null>(null);
  const [createdLink, setCreatedLink] = useState('');
  const [expiresInHours, setExpiresInHours] = useState(720);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function load(): Promise<void> {
    try {
      const result = await api<{ grants: MaintenanceGrant[] }>('/api/admin/maintenance-access');
      setGrants(result.grants);
    } catch {
      // Silencieux : ce bloc est secondaire à l'écran, une erreur ici ne doit
      // pas empêcher de voir les anomalies en dessous.
    }
  }

  useEffect(() => { void load(); }, []);

  async function create(): Promise<void> {
    setBusy(true);
    setError('');
    setCreatedLink('');
    try {
      const result = await api<{ link: string }>('/api/admin/maintenance-access', {
        method: 'POST',
        body: JSON.stringify({ expiresInHours }),
      });
      setCreatedLink(result.link);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Création impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(grantId: string): Promise<void> {
    if (!window.confirm('Révoquer immédiatement ce lien ?')) return;
    try {
      await api(`/api/admin/grants/${grantId}/revoke`, { method: 'POST' });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Révocation impossible.');
    }
  }

  async function copyLink(): Promise<void> {
    await navigator.clipboard.writeText(createdLink);
  }

  const active = grants?.filter((grant) => !grant.revoked && grant.expiresAt > new Date().toISOString()) ?? [];

  const body = (
    <>
      <p className="section-intro">
        Ouvre uniquement l'écran Entretien : déclarer une réparation avec sa photo, rien d'autre. Envoyez le lien
        vous-même (WhatsApp, SMS…), il n'est jamais transmis par e-mail automatiquement.
      </p>
      {active.length > 0 && (
        <div className="record-list">
          {active.map((grant) => (
            <article className="record-card" key={grant.id}>
              <div className="record-heading">
                <div><strong>Lien actif</strong><span className="record-code">créé le {shortDate(grant.createdAt)}</span></div>
                <button className="icon-button danger" title="Révoquer" aria-label="Révoquer" onClick={() => void revoke(grant.id)}>×</button>
              </div>
              <p className="record-line muted"><span>expire le {shortDate(grant.expiresAt)}</span></p>
            </article>
          ))}
          {/* Le jeton n'est stocké que sous forme d'empreinte : le lien complet
              n'existe qu'au moment de sa création. Le dire ici évite de le
              chercher, et de croire à une perte. */}
          <p className="field-help">
            Le lien complet ne s'affiche qu'une fois, à sa création : le serveur n'en garde qu'une empreinte,
            jamais l'adresse elle-même. Si vous l'avez perdu, révoquez celui-ci et créez-en un nouveau.
          </p>
        </div>
      )}
      <div className="inline-form">
        <label>Valable
          <select value={expiresInHours} onChange={(event) => setExpiresInHours(Number(event.target.value))}>
            <option value={168}>7 jours</option>
            <option value={720}>30 jours</option>
            <option value={2160}>90 jours</option>
          </select>
        </label>
        <button type="button" className="button primary compact" disabled={busy} onClick={() => void create()}>
          {busy ? 'Création…' : 'Créer un nouveau lien'}
        </button>
      </div>
      {createdLink && (
        <div className="link-result" role="status">
          <div><strong>Lien prêt</strong><span>Envoyez-le uniquement à la personne concernée.</span></div>
          <button className="button compact" onClick={() => void copyLink()}>Copier</button>
          <code>{createdLink}</code>
        </div>
      )}
      {error && <div className="inline-message error" role="alert">{error}</div>}
    </>
  );

  if (embedded) return body;
  return (
    <details className="access-panel">
      <summary>Lien pour une intervenante (ex. Aurélie)</summary>
      {body}
    </details>
  );
}
